// src/services/CommonService.js
import ApiService from './api'

class CommonService {
  // Get all system page access permissions
  async getAllSystemPageAccess() {
    try {
      const response = await ApiService.get('/common/system-permissions')
      return response
    } catch (error) {
      console.error('Get system permissions error:', error)
      return { Success: false, Message: error.message || "Failed to fetch system permissions" }
    }
  }

  // Get dashboard statistics
  async getDashboardStats() {
    try {
      const response = await ApiService.get('/dashboard/stats')
      return response
    } catch (error) {
      console.error('Get dashboard stats error:', error)
      return { Success: false, Message: error.message || "Failed to fetch dashboard statistics" }
    }
  }

  // Get recent activities
  async getRecentActivities(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const endpoint = queryParams ? `/dashboard/activities?${queryParams}` : '/dashboard/activities'
      const response = await ApiService.get(endpoint)
      return response
    } catch (error) {
      console.error('Get recent activities error:', error)
      return { Success: false, Message: error.message || "Failed to fetch recent activities" }
    }
  }

  // Get chart data
  async getChartData(chartType) {
    try {
      const response = await ApiService.get(`/dashboard/charts/${chartType}`)
      return response
    } catch (error) {
      console.error('Get chart data error:', error)
      return { Success: false, Message: error.message || "Failed to fetch chart data" }
    }
  }

  // Get application settings
  async getAppSettings() {
    try {
      const response = await ApiService.get('/settings')
      return response
    } catch (error) {
      console.error('Get app settings error:', error)
      return { Success: false, Message: error.message || "Failed to fetch application settings" }
    }
  }

  // Update application settings
  async updateAppSettings(settings) {
    try {
      const response = await ApiService.put('/settings', {}, settings)
      return response
    } catch (error) {
      console.error('Update app settings error:', error)
      return { Success: false, Message: error.message || "Failed to update application settings" }
    }
  }

  // Get system health status
  async getSystemHealth() {
    try {
      const response = await ApiService.get('/system/health')
      return response
    } catch (error) {
      console.error('Get system health error:', error)
      return { Success: false, Message: error.message || "Failed to fetch system health" }
    }
  }

  // Get countries list
  async getCountries() {
    try {
      const response = await ApiService.get('/common/countries')
      return response
    } catch (error) {
      console.error('Get countries error:', error)
      return { Success: false, Message: error.message || "Failed to fetch countries" }
    }
  }

  // Get timezones list
  async getTimezones() {
    try {
      const response = await ApiService.get('/common/timezones')
      return response
    } catch (error) {
      console.error('Get timezones error:', error)
      return { Success: false, Message: error.message || "Failed to fetch timezones" }
    }
  }

  // Get languages list
  async getLanguages() {
    try {
      const response = await ApiService.get('/common/languages')
      return response
    } catch (error) {
      console.error('Get languages error:', error)
      return { Success: false, Message: error.message || "Failed to fetch languages" }
    }
  }

  // Upload file
  async uploadFile(file, folder = 'general') {
    try {
      const response = await ApiService.uploadFile('/files/upload', file, { folder })
      return response
    } catch (error) {
      console.error('Upload file error:', error)
      return { Success: false, Message: error.message || "Failed to upload file" }
    }
  }

  // Delete file
  async deleteFile(fileId) {
    try {
      const response = await ApiService.delete(`/files/${fileId}`)
      return response
    } catch (error) {
      console.error('Delete file error:', error)
      return { Success: false, Message: error.message || "Failed to delete file" }
    }
  }

  // Send notification
  async sendNotification(notificationData) {
    try {
      const response = await ApiService.post('/notifications/send', {}, notificationData)
      return response
    } catch (error) {
      console.error('Send notification error:', error)
      return { Success: false, Message: error.message || "Failed to send notification" }
    }
  }

  // Get notifications
  async getNotifications(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const endpoint = queryParams ? `/notifications?${queryParams}` : '/notifications'
      const response = await ApiService.get(endpoint)
      return response
    } catch (error) {
      console.error('Get notifications error:', error)
      return { Success: false, Message: error.message || "Failed to fetch notifications" }
    }
  }

  // Mark notification as read
  async markNotificationAsRead(notificationId) {
    try {
      const response = await ApiService.put(`/notifications/${notificationId}/read`)
      return response
    } catch (error) {
      console.error('Mark notification as read error:', error)
      return { Success: false, Message: error.message || "Failed to mark notification as read" }
    }
  }

  // Mark all notifications as read
  async markAllNotificationsAsRead() {
    try {
      const response = await ApiService.put('/notifications/mark-all-read')
      return response
    } catch (error) {
      console.error('Mark all notifications as read error:', error)
      return { Success: false, Message: error.message || "Failed to mark all notifications as read" }
    }
  }

  // Delete notification
  async deleteNotification(notificationId) {
    try {
      const response = await ApiService.delete(`/notifications/${notificationId}`)
      return response
    } catch (error) {
      console.error('Delete notification error:', error)
      return { Success: false, Message: error.message || "Failed to delete notification" }
    }
  }

  // Generate report
  async generateReport(reportType, params = {}) {
    try {
      const response = await ApiService.post(`/reports/generate/${reportType}`, {}, params)
      return response
    } catch (error) {
      console.error('Generate report error:', error)
      return { Success: false, Message: error.message || "Failed to generate report" }
    }
  }

  // Download report
  async downloadReport(reportId) {
    try {
      const response = await ApiService.get(`/reports/download/${reportId}`)
      return response
    } catch (error) {
      console.error('Download report error:', error)
      return { Success: false, Message: error.message || "Failed to download report" }
    }
  }

  // Get audit logs
  async getAuditLogs(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const endpoint = queryParams ? `/audit/logs?${queryParams}` : '/audit/logs'
      const response = await ApiService.get(endpoint)
      return response
    } catch (error) {
      console.error('Get audit logs error:', error)
      return { Success: false, Message: error.message || "Failed to fetch audit logs" }
    }
  }

  // Search across the system
  async globalSearch(searchTerm, filters = {}) {
    try {
      const params = { q: searchTerm, ...filters }
      const queryParams = new URLSearchParams(params).toString()
      const response = await ApiService.get(`/search?${queryParams}`)
      return response
    } catch (error) {
      console.error('Global search error:', error)
      return { Success: false, Message: error.message || "Failed to perform search" }
    }
  }

  // Get system configuration
  async getSystemConfig() {
    try {
      const response = await ApiService.get('/system/config')
      return response
    } catch (error) {
      console.error('Get system config error:', error)
      return { Success: false, Message: error.message || "Failed to fetch system configuration" }
    }
  }

  // Update system configuration
  async updateSystemConfig(config) {
    try {
      const response = await ApiService.put('/system/config', {}, config)
      return response
    } catch (error) {
      console.error('Update system config error:', error)
      return { Success: false, Message: error.message || "Failed to update system configuration" }
    }
  }

  // Get system logs
  async getSystemLogs(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString()
      const endpoint = queryParams ? `/system/logs?${queryParams}` : '/system/logs'
      const response = await ApiService.get(endpoint)
      return response
    } catch (error) {
      console.error('Get system logs error:', error)
      return { Success: false, Message: error.message || "Failed to fetch system logs" }
    }
  }

  // Clear cache
  async clearCache() {
    try {
      const response = await ApiService.post('/system/clear-cache')
      return response
    } catch (error) {
      console.error('Clear cache error:', error)
      return { Success: false, Message: error.message || "Failed to clear cache" }
    }
  }

  // Backup database
  async backupDatabase() {
    try {
      const response = await ApiService.post('/system/backup')
      return response
    } catch (error) {
      console.error('Backup database error:', error)
      return { Success: false, Message: error.message || "Failed to backup database" }
    }
  }

  // Get backup history
  async getBackupHistory() {
    try {
      const response = await ApiService.get('/system/backups')
      return response
    } catch (error) {
      console.error('Get backup history error:', error)
      return { Success: false, Message: error.message || "Failed to fetch backup history" }
    }
  }

  // Restore from backup
  async restoreFromBackup(backupId) {
    try {
      const response = await ApiService.post(`/system/restore/${backupId}`)
      return response
    } catch (error) {
      console.error('Restore from backup error:', error)
      return { Success: false, Message: error.message || "Failed to restore from backup" }
    }
  }

  // Get system metrics
  async getSystemMetrics() {
    try {
      const response = await ApiService.get('/system/metrics')
      return response
    } catch (error) {
      console.error('Get system metrics error:', error)
      return { Success: false, Message: error.message || "Failed to fetch system metrics" }
    }
  }

  // Send test email
  async sendTestEmail(emailData) {
    try {
      const response = await ApiService.post('/system/test-email', {}, emailData)
      return response
    } catch (error) {
      console.error('Send test email error:', error)
      return { Success: false, Message: error.message || "Failed to send test email" }
    }
  }
}

// Export singleton instance
export default new CommonService()
